import React, { useEffect, useState } from "react";
import {
  Button, Card, CardHeader, CardBody, CardTitle,
  Row, Col, FormGroup, Label, Input,
  Modal, ModalHeader, ModalBody, ModalFooter, Table
} from "reactstrap";
import Select from "react-select";
import ReactLoading from "react-loading";
import { Toaster } from "react-hot-toast";
import { overlayStyles, loaderContainerStyles } from "styles/commonStyles";
import { useNotifications } from "hooks/useNotifications";
import { createPayment } from "../Clients/service/paymentsService";
import { createVenta } from "../Clients/service/ventasService";
import { fetchAllMemberships } from "../Clients/service/membershipService";
import { fetchAllClients } from "../Clients/service/clientsService";
import { useSede } from "context/SedeContext";
import useSedeRefresh from "../../../hooks/useSedeRefresh";

const normalize = (text) =>
  text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

function PaymentsRegister() {
  const { addSedeParams, addSedeHeaders, getSedeIds } = useSede();
  const [loading, setLoading] = useState(false);
  const [isLoadedSuccess, setIsLoadedSuccess] = useState(false);
  const [error, setError] = useState(null);

  const [clients, setClients] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientId, setClientId] = useState(null);

  const [isMembership, setIsMembership] = useState(true);
  const [saleDate, setSaleDate] = useState('');

  const [memberships, setMemberships] = useState([]);
  const [selectedMembership, setSelectedMembership] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentMonth, setPaymentMonth] = useState('');
  const [paymentYear, setPaymentYear] = useState(new Date().getFullYear());
  const [validUntil, setValidUntil] = useState('');

  // Función para calcular la fecha de vencimiento hasta el final del mes seleccionado
  const calculateValidUntil = (month, year) => {
    if (!month || !year) return '';
    
    const monthNum = parseInt(month);
    const yearNum = parseInt(year);
    
    // Calcular el último día del mes seleccionado
    let lastDayOfMonth;
    if (monthNum === 2) {
      // Verificar año bisiesto
      if (yearNum % 4 === 0 && (yearNum % 100 !== 0 || yearNum % 400 === 0)) {
        lastDayOfMonth = 29;
      } else {
        lastDayOfMonth = 28;
      }
    } else if (monthNum === 4 || monthNum === 6 || monthNum === 9 || monthNum === 11) {
      lastDayOfMonth = 30;
    } else {
      lastDayOfMonth = 31;
    }
    
    // Crear la fecha del último día del mes seleccionado
    const validUntilDate = new Date(yearNum, monthNum - 1, lastDayOfMonth);
    return validUntilDate.toISOString().split('T')[0];
  };

  // Función para calcular la fecha de inicio del Mes a Pagar
  const calculatePaymentStartDate = (month, year) => {
    if (!month || !year) return '';
    
    const monthNum = parseInt(month);
    const yearNum = parseInt(year);
    
    // Crear la fecha del primer día del mes seleccionado
    const startDate = new Date(yearNum, monthNum - 1, 1);
    return startDate.toISOString().split('T')[0];
  };

  // Manejar cambio en Mes a Pagar y calcular automáticamente valid_until
  const handlePaymentMonthChange = (e) => {
    const newMonth = e.target.value;
    setPaymentMonth(newMonth);
    
    // Calcular automáticamente la fecha de vencimiento
    const calculatedValidUntil = calculateValidUntil(newMonth, paymentYear);
    setValidUntil(calculatedValidUntil);
  };

  // Manejar cambio en año de pago y calcular automáticamente valid_until
  const handlePaymentYearChange = (e) => {
    const newYear = e.target.value;
    setPaymentYear(newYear);
    
    // Calcular automáticamente la fecha de vencimiento
    const calculatedValidUntil = calculateValidUntil(paymentMonth, newYear);
    setValidUntil(calculatedValidUntil);
  };
  const [paymentMethod, setPaymentMethod] = useState('');

  const [productName, setProductName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [pricePerUnit, setPricePerUnit] = useState('');
  const [ventaNotes, setVentaNotes] = useState('');

  const [paymentList, setPaymentList] = useState([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const toggleConfirmModal = () => setConfirmModalOpen(!confirmModalOpen);

  const fetchData = async () => {
    setLoading(true);
    try {
      const sedeParams = addSedeParams({});
      const sedeHeaders = addSedeHeaders({});
      const [clientData, membershipData] = await Promise.all([
        fetchAllClients(sedeParams, sedeHeaders),
        fetchAllMemberships(sedeParams, sedeHeaders),
      ]);
      setClients(clientData);
      setMemberships(membershipData);
      setClientOptions(clientData.map(c => ({
        value: c.id,
        label: `${c.first_name} ${c.last_name} - ${c.email}`
      })));
    } catch (err) {
      console.error("Error cargando datos", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Escuchar cambios de sede y refrescar automáticamente
  useSedeRefresh(fetchData, [addSedeParams, addSedeHeaders]);

  const handleAddToList = () => {
    if (!clientId || !paymentMethod) return;

    const selectedClient = clients.find(c => c.id === clientId);
    const clientName = selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : `ID: ${clientId}`;

    if (isMembership) {
      if (!selectedMembership || !paymentAmount || !paymentDate || !paymentMonth) return;
      const membershipName = memberships.find(m => m.id === parseInt(selectedMembership))?.name;
      setPaymentList((prev) => [
        ...prev,
        {
          type: 'membership',
          client_id: clientId,
          client_name: clientName,
          membership_id: selectedMembership,
          membership_name: membershipName,
          amount: paymentAmount,
          date_paid: paymentDate, // Fecha real del pago (solo para referencia)
          payment_month: paymentMonth,
          payment_year: paymentYear,
          payment_start_date: calculatePaymentStartDate(paymentMonth, paymentYear), // Inicio del Mes a Pagar
          valid_until: validUntil,
          payment_method: paymentMethod
        }
      ]);
    } else {
      if (!productName || !quantity || !pricePerUnit) return;
      setPaymentList((prev) => [
        ...prev,
        {
          type: 'venta',
          client_id: clientId,
          client_name: clientName,
          product_name: productName,
          quantity,
          price_per_unit: pricePerUnit,
          total_amount: (quantity * parseFloat(pricePerUnit)).toFixed(2),
          payment_method: paymentMethod,
          notes: ventaNotes,
          date_sold: paymentDate
        }
      ]);
    }

    // Limpiar campos
    setClientId(null);
    setSelectedMembership('');
    setPaymentAmount('');
    setPaymentDate('');
    setPaymentMonth('');
    setPaymentYear(new Date().getFullYear());
    setValidUntil(''); // Se calculará automáticamente cuando se seleccione mes/año
    setPaymentMethod('');
    setProductName('');
    setQuantity(1);
    setPricePerUnit('');
    setVentaNotes('');
  };

  const handleRemoveFromList = (index) => {
    setPaymentList(prev => prev.filter((_, i) => i !== index));
  };

  const handleRegisterAll = async () => {
    setLoading(true);
    try {
      // Get the first selected sede ID (assuming single sede for payments)
      const sedeIds = getSedeIds();
      const sedeId = sedeIds.length > 0 ? sedeIds[0] : null;
      
      for (let p of paymentList) {
        if (p.type === 'membership') {
          await createPayment({
            client_id: p.client_id,
            membership_id: p.membership_id,
            amount: p.amount,
            date_paid: new Date(`${p.payment_start_date}T00:00:00`).toLocaleString("sv-SE", { hour12: false }).replace(" ", "T"),
            valid_from: p.date_paid, // Usar fecha de pago real, no el primer día del mes
            valid_until: p.valid_until,
            payment_method: p.payment_method
          }, sedeId);
        } else {
          await createVenta({
            client_id: p.client_id,
            product_name: p.product_name,
            quantity: p.quantity,
            date_sold: new Date(`${saleDate}T00:00:00`).toLocaleString("sv-SE", { hour12: false }).replace(" ", "T"),
            price_per_unit: p.price_per_unit,
            total_amount: (quantity * parseFloat(p.price_per_unit)).toFixed(2),
            payment_method: p.payment_method,
            notes: p.notes
          }, sedeId);
        }
      }
      setIsLoadedSuccess(true);
      setPaymentList([]);
      toggleConfirmModal();
    } catch (err) {
      const data = err?.response?.data;
      const message =
        data?.detail ||
        data?.error ||
        data?.message ||
        (Array.isArray(data?.non_field_errors) ? data.non_field_errors.join(" ") : null) ||
        err.message ||
        "Error al registrar.";
      setError(new Error(message));
    } finally {
      setLoading(false);
    }
  };

  const notificationMessages = {
    success: "Pagos/ventas registrados exitosamente.",
    error: (e) => e.message || "Error al registrar.",
  };

  useNotifications(isLoadedSuccess, error, notificationMessages);

  const customFilterOption = (option, inputValue) => {
    return normalize(option.label).includes(normalize(inputValue));
  };

  return (
    <>
      {loading && (
        <div style={overlayStyles}>
          <div style={loaderContainerStyles}>
            <ReactLoading type="spin" color="white" height="40%" />
          </div>
        </div>
      )}
      <Toaster position="top-right" />

      <Modal isOpen={confirmModalOpen} toggle={toggleConfirmModal} centered>
        <ModalHeader toggle={toggleConfirmModal}>Confirmar Registro de Pagos</ModalHeader>
        <ModalBody>
          {paymentList.length === 0 ? (
            <p>No hay pagos para guardar.</p>
          ) : (
            <ul>
              {paymentList.map((p, idx) => (
                <li key={idx}>
                  {p.type === 'membership'
                    ? `${p.client_name} - ${p.membership_name} Q${p.amount} (${p.payment_method})`
                    : `${p.client_name} - ${p.product_name} x${p.quantity} Q${p.total_amount} (${p.payment_method})`
                  }
                </li>
              ))}
            </ul>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleConfirmModal}>Cancelar</Button>
          <Button style={{ backgroundColor: "#7F6552", borderColor: "#7F6552" }} onClick={handleRegisterAll}>
            Registrar Pagos
          </Button>
        </ModalFooter>
      </Modal>

      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader><CardTitle tag="h4">Registrar Pago o Venta</CardTitle></CardHeader>
              <CardBody>
                <FormGroup tag="fieldset">
                  <legend>Tipo de Pago</legend>
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="radio"
                        name="tipoRegistro"
                        checked={isMembership}
                        onChange={() => setIsMembership(true)}
                      />{' '}
                      Pago de Membresía
                    </Label>
                  </FormGroup>
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="radio"
                        name="tipoRegistro"
                        checked={!isMembership}
                        onChange={() => setIsMembership(false)}
                      />{' '}
                      Pagos Adicionales
                    </Label>
                  </FormGroup>
                </FormGroup>

                <FormGroup>
                  <Label>Alumno</Label>
                  <Select
                    options={clientOptions}
                    value={clientOptions.find(o => o.value === clientId) || null}
                    onChange={(selected) => setClientId(selected ? selected.value : null)}
                    placeholder="Buscar alumno..."
                    isClearable
                    filterOption={customFilterOption}
                  />
                </FormGroup>

                {isMembership ? (
                  <>
                    <FormGroup>
                      <Label>Membresía</Label>
                      <Input
                        type="select"
                        value={selectedMembership || ''}
                        onChange={(e) => {
                          const id = e.target.value;
                          setSelectedMembership(id);
                          const selected = memberships.find(m => m.id === parseInt(id));
                          if (selected) setPaymentAmount(selected.price);
                        }}
                      >
                        <option value="" disabled>-- Elegir Membresía --</option>
                        {memberships.map(m => (
                          <option key={m.id} value={m.id}>
                            {m.name} - Q{parseFloat(m.price).toFixed(2)}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>

                    <FormGroup>
                      <Label>Método de Pago</Label>
                      <Input
                        type="select"
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                      >
                        <option value="" disabled>-- Selecciona método de pago --</option>
                        <option value="Efectivo">Efectivo</option>
                        <option value="Transferencia">Transferencia</option>
                        <option value="VisaLink">VisaLink</option>
                      </Input>
                    </FormGroup>

                    <FormGroup>
                      <Label>Monto</Label>
                      <Input type="number" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} />
                    </FormGroup>

                    <FormGroup>
                      <Label>Fecha de Pago</Label>
                      <Input
                        type="date"
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                      />
                    </FormGroup>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Mes a Pagar</Label>
                          <Input
                            type="select"
                            value={paymentMonth}
                            onChange={handlePaymentMonthChange}
                          >
                            <option value="">Seleccionar mes</option>
                            <option value="1">Enero</option>
                            <option value="2">Febrero</option>
                            <option value="3">Marzo</option>
                            <option value="4">Abril</option>
                            <option value="5">Mayo</option>
                            <option value="6">Junio</option>
                            <option value="7">Julio</option>
                            <option value="8">Agosto</option>
                            <option value="9">Septiembre</option>
                            <option value="10">Octubre</option>
                            <option value="11">Noviembre</option>
                            <option value="12">Diciembre</option>
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Año de Pago</Label>
                          <Input
                            type="number"
                            value={paymentYear}
                            onChange={handlePaymentYearChange}
                            min="2020"
                            max="2030"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <FormGroup>
                      <Label>Fecha de Vencimiento</Label>
                      <Input 
                        type="date" 
                        value={validUntil} 
                        readOnly
                        style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                      />
                      <small className="text-muted">
                        Calculado automáticamente hasta el final del mes seleccionado
                      </small>
                    </FormGroup>
                  </>
                ) : (
                  <>
                    <FormGroup>
                      <Label>Pago de: </Label>
                      <Input
                        type="select"
                        value={productName ? productName : ''}
                        onChange={(e) => setProductName(e.target.value)}
                      >
                        <option value="" disabled>-- Selecciona Concepto --</option>
                        <option value="Penalizacion">Penalización</option>
                        <option value="Calcetas de Pilates">Calcetas de Pilates</option>
                      </Input>
                    </FormGroup>
                    <FormGroup>
                      <Label>Cantidad</Label>
                      <Input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                      <Label>Fecha de Pago</Label>
                      <Input
                        type="date"
                        value={saleDate}
                        onChange={(e) => setSaleDate(e.target.value)}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Precio Por Unidad</Label>
                      <Input type="number" value={pricePerUnit} onChange={(e) => setPricePerUnit(e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                      <Label>Método de Pago</Label>
                      <Input
                        type="select"
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                      >
                        <option value="" disabled>-- Selecciona método de pago --</option>
                        <option value="Efectivo">Efectivo</option>
                        <option value="Transferencia">Transferencia</option>
                        <option value="VisaLink">VisaLink</option>
                      </Input>
                    </FormGroup>
                  </>
                )}

                <Button
                  style={{ backgroundColor: "#7F6552", color: "white", borderColor: "#7F6552" }}
                  onClick={handleAddToList}
                >
                  Agregar a la lista
                </Button>

                {paymentList.length > 0 && (
                  <>
                    <hr />
                    <h5>Pagos por confirmar:</h5>
                    <Table bordered hover responsive>
                      <thead className="thead-light">
                        <tr>
                          <th>#</th>
                          <th>Alumno</th>
                          <th>Detalle</th>
                          <th>Monto</th>
                          <th>Método</th>
                          <th>Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentList.map((p, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td>{p.client_name}</td>
                            <td>
                              {p.type === 'membership'
                                ? `${p.membership_name} (${p.date_paid} → ${p.valid_until})`
                                : `${p.product_name} x${p.quantity}`}
                            </td>
                            <td>Q{p.type === 'membership' ? parseFloat(p.amount).toFixed(2) : parseFloat(p.total_amount).toFixed(2)}</td>
                            <td>{p.payment_method}</td>
                            <td>
                              <Button color="danger" size="sm" className="btn-icon btn-link remove" onClick={() => handleRemoveFromList(idx)}>
                                <i className="fa fa-remove" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    <Button
                      style={{ backgroundColor: "#7F6552", color: "white", borderColor: "#7F6552" }}
                      onClick={toggleConfirmModal}
                    >
                      Confirmar y Registrar Todos
                    </Button>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default PaymentsRegister;
